import { useCallback, useEffect, useState } from 'react';

import {
  buildConsentState,
  getConsent,
  saveConsent,
  resetConsent,
} from '../lib/cookieConsent';
import { applyThirdPartyScripts } from '../lib/thirdParty';
import {
  CONSENT_CHANGED_EVENT,
  type ConsentState,
  type CookieCategory,
  type CookieConsentConfig,
} from '../types/cookies.types';

const defaultDraft = (version: string): ConsentState =>
  buildConsentState({ analytics: false, marketing: false, preferences: false }, version);

/**
 * React hook for reading and updating GDPR cookie consent.
 */
export function useCookieConsent(config: CookieConsentConfig) {
  const version = config.policyVersion;

  const [consentState, setConsentState] = useState<ConsentState>(() =>
    getConsent(version) ?? defaultDraft(version),
  );
  const [hasResponded, setHasResponded] = useState(() => getConsent(version) !== null);
  const [draft, setDraft] = useState<ConsentState>(consentState);

  const syncFromStorage = useCallback(() => {
    const stored = getConsent(version);
    if (stored) {
      setConsentState(stored);
      setDraft(stored);
      setHasResponded(true);
      applyThirdPartyScripts(stored, config);
    } else {
      const fresh = defaultDraft(version);
      setConsentState(fresh);
      setDraft(fresh);
      setHasResponded(false);
    }
  }, [config, version]);

  useEffect(() => {
    syncFromStorage();
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<ConsentState>).detail;
      if (detail) {
        setConsentState(detail);
        setDraft(detail);
        setHasResponded(getConsent(version) !== null);
      }
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChanged);
  }, [syncFromStorage, version]);

  const persist = useCallback(
    (state: ConsentState) => {
      saveConsent(state);
      setConsentState(state);
      setDraft(state);
      setHasResponded(true);
      applyThirdPartyScripts(state, config);
    },
    [config],
  );

  const acceptAll = useCallback(() => {
    persist(buildConsentState({ analytics: true, marketing: true, preferences: true }, version));
  }, [persist, version]);

  const rejectAll = useCallback(() => {
    persist(buildConsentState({ analytics: false, marketing: false, preferences: false }, version));
  }, [persist, version]);

  const updateConsent = useCallback((category: CookieCategory, value: boolean) => {
    if (category === 'necessary') {
      return;
    }
    setDraft((prev) => ({ ...prev, [category]: value }));
  }, []);

  const savePreferences = useCallback(() => {
    persist({ ...draft, necessary: true, version, timestamp: Date.now() });
  }, [draft, persist, version]);

  const openPreferences = useCallback(() => {
    setDraft(consentState);
  }, [consentState]);

  const clearConsent = useCallback(() => {
    resetConsent(version);
    syncFromStorage();
  }, [syncFromStorage, version]);

  return {
    consentState,
    draft,
    hasResponded,
    acceptAll,
    rejectAll,
    updateConsent,
    savePreferences,
    openPreferences,
    clearConsent,
  };
}
